import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { IconButton, OutlinedInput, Stack } from '@mui/material';
import { ReactNode, useEffect, useRef, useState } from 'react';

type NumberInputProps = {
  value: number;
  step: number;
  min: number;
  endAdorment?: ReactNode;
  error?: boolean;
  // the component must ensure that only coherent values according
  // to the min/max/step params are emitted
  onChanged?: (newValue: number) => void;
  onInvalidInput?: () => void;
};

export function NumberInput(props: NumberInputProps) {
  const [count, setCount] = useState<string>('');
  const firmValue = useRef<number>();

  useEffect(() => {
    firmValue.current = props.value;
    setCount(`${props.value}`);
  }, [props.value]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const number = event.target.value;
    setCount(number);
  };

  const handleBlur = () => {
    const currentValue = Number(count);
    if (isNaN(currentValue) || currentValue % props.step !== 0) {
      props.onInvalidInput?.();
      setCount(`${props.value}`);
    } else props.onChanged?.(currentValue);
  };

  const handleClick = (type: 'plus' | 'less') => {
    const currentValue = Number(firmValue.current);
    if (isNaN(currentValue)) return;
    const increment = props.step * (type === 'plus' ? 1 : -1);
    const newValue = currentValue + increment;
    if (newValue < props.min) return;
    props.onChanged?.(newValue);
  };

  return (
    <Stack
      direction="row"
      sx={{ minWidth: '160px', alignItems: 'center', gap: '8px' }}
    >
      <IconButton
        aria-label="less"
        onClick={() => handleClick('less')}
        sx={{ width: '32px', height: '32px' }}
      >
        <RemoveIcon />
      </IconButton>
      <OutlinedInput
        value={count}
        onChange={handleChange}
        onBlur={handleBlur}
        endAdornment={props.endAdorment}
        error={props.error}
      />
      <IconButton
        aria-label="plus"
        onClick={() => handleClick('plus')}
        sx={{ width: '32px', height: '32px' }}
      >
        <AddIcon />
      </IconButton>
    </Stack>
  );
}
